import { Navigate, Outlet, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import {
  PLATFORM_ME_KEY,
  isDashboardPrincipal,
  useAuth,
  useCustomerSession,
  useUser,
} from '../lib/auth.js';

function Loading() {
  return (
    <div className="grid min-h-dvh place-items-center" role="status" aria-live="polite">
      <span className="text-fg-muted">Loading…</span>
    </div>
  );
}

/**
 * Route guards for the three surfaces — AGENTS.md §7.
 *
 * These are a routing convenience, not a security control. Every one of these
 * checks is repeated server-side, because a guard that only exists in the
 * browser is a guard an attacker skips by calling the API directly. What these
 * buy is that a signed-out merchant sees a login form instead of a flash of
 * empty dashboard followed by a wall of 401s.
 */
export function RequireUser({ children }: { children?: ReactNode }) {
  const { principal, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <Loading />;
  // Support inside a merchant's account counts — features/PLATFORM-ADMIN.md.
  if (!isDashboardPrincipal(principal)) {
    // The attempted path rides along so the login can return them to it,
    // rather than dropping them on the dashboard home.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

export function RequireStaff({ children }: { children?: ReactNode }) {
  const { principal, isLoading } = useAuth();

  if (isLoading) return <Loading />;
  if (principal?.type !== 'STAFF') return <Navigate to="/staff/login" replace />;
  return <>{children ?? <Outlet />}</>;
}

export function RequireCustomer({ children }: { children?: ReactNode }) {
  // Its own session, not `useAuth` — a dashboard cookie in the same browser
  // would otherwise win and send a signed-in customer back to login (LOY-8).
  const { customer, isLoading } = useCustomerSession();
  const location = useLocation();

  if (isLoading) return <Loading />;
  if (!customer) {
    return <Navigate to="/c/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * Role check within the dashboard. Owner-only screens are billing and
 * destructive settings; a manager reaching one sees a refusal rather than a
 * redirect, because bouncing them somewhere else looks like a broken link.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: Array<'OWNER' | 'MANAGER' | 'PLATFORM_ADMIN'>;
  children?: ReactNode;
}) {
  const { isLoading } = useAuth();
  const user = useUser();

  if (isLoading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;

  if (!roles.includes(user.role as 'OWNER' | 'MANAGER' | 'PLATFORM_ADMIN')) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className="text-xl font-semibold text-fg">Not available to your account</h1>
        <p className="mt-2 text-fg-muted">
          Ask the account owner if you need access to this.
        </p>
      </div>
    );
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * The platform admin surface — features/PLATFORM-ADMIN.md.
 *
 * Its own session, asked of `/platform/me`, because the admin cookie is not one
 * `/auth/me` describes. Without this `/platform` rendered its chrome to anyone,
 * every request under it failing with a 401 and nothing saying why.
 */
export function RequirePlatformAdmin({ children }: { children?: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: PLATFORM_ME_KEY,
    queryFn: async () => {
      try {
        return await api<{ admin: { id: string } }>('/platform/me');
      } catch (error) {
        // Signed out is an answer, not a failure to retry.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: 2,
    staleTime: 60_000,
  });
  const location = useLocation();

  if (isLoading) return <Loading />;
  if (!data) return <Navigate to="/platform/login" replace state={{ from: location.pathname }} />;
  return <>{children ?? <Outlet />}</>;
}
